import React from 'react';

import { $config } from '@client/core/atoms/config.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import {
  AdServer,
  ContainerId,
  type PlacementId,
  PlacementStatus,
  debugLog
} from '@schibsted-nmp/advertising-shared';

import { SponsoredProductCarousel } from '../ads/SponsoredProductCarousel.js';
import {
  DsaBrand,
  type RetailMediaPayload,
  type RetailMediaProduct
} from '../types/index.js';
import {
  callApi,
  RateLimitError,
  getVisitorId
} from '../utils/RetailMediaApi.js';
import { clearSlot, renderIntoSlot } from '../utils/slotRoot.js';

/**
 * The carousel pages three cards at a time, so it asks for enough products to
 * fill a few pages. The server returns fewer when the keyword ladder runs dry.
 */
const SPONSORED_PRODUCTS_COUNT = 10;

const request = async (
  placementId: PlacementId,
  searchQuery: string,
  categories: string[] | undefined,
  brandNames?: Record<string, string>
) => {
  const brand = $config.get()?.brand;
  const dsaBrand = DsaBrand.is(brand) ? brand : undefined;

  try {
    const payload: RetailMediaPayload = {
      zone: 'recommerce_search',
      visitorId: getVisitorId(),
      sponsoredProductsCount: SPONSORED_PRODUCTS_COUNT,
      searchTerm: searchQuery,
      ...(categories && categories.length > 0 && { categories }),
      ...(brandNames &&
        Object.keys(brandNames).length > 0 && {
          brandNames: Object.values(brandNames)
        }),
      debug: true,
      userAgent: navigator.userAgent,
      ...(dsaBrand && { dsaBrand })
    };

    updatePlacementKeyValueById(placementId, 'creativeType', 'native-retail');
    updatePlacementKeyValueById(placementId, 'status', 'request');

    const dataPromise = callApi(payload);

    debugLog(
      `retail_media -> ad requested for placement: ${placementId}`,
      payload
    );

    updatePlacementKeyValueById(placementId, 'status', 'requested');
    updateMetricByKey(placementId, PlacementStatus.AdRequested);

    const data = await dataPromise;

    debugLog('retail_media -> API response:', data);

    setTimeout(() => {
      try {
        renderSponsoredProductCarousel(
          placementId,
          data.sponsoredProducts,
          categories
        );
      } catch (error) {
        debugLog(
          'retail_media -> Error rendering sponsored carousel ad:',
          error
        );

        updateMetricByKey(placementId, PlacementStatus.AdNoFill);
        updatePlacementKeyValueById(placementId, 'status', 'error');
      }
    }, 1);
  } catch (error) {
    // Ignore rate-limited requests - the first request will handle rendering
    if (error instanceof RateLimitError) {
      debugLog('retail_media -> Rate-limited request ignored:', error.message);
      return;
    }

    debugLog('retail_media -> Error in request:', error);
    updateMetricByKey(placementId, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(placementId, 'status', 'error');
  }
};

export function triggerCarouselRequest(
  placementId: PlacementId,
  searchQuery: string | string[] | undefined,
  categories: string[] | undefined,
  brandNames?: Record<string, string>
) {
  const normalizedQuery = Array.isArray(searchQuery)
    ? searchQuery[0]
    : searchQuery;

  void request(placementId, normalizedQuery || '', categories, brandNames);
}

function renderSponsoredProductCarousel(
  placementId: PlacementId,
  products?: Array<RetailMediaProduct>,
  categories?: string[]
) {
  const containerId = ContainerId.fromPlacementId(
    placementId,
    AdServer.Retailmedia
  );

  const containerElement = document.getElementById(containerId);

  if (!containerElement) {
    debugLog('retail_media -> Container element not found:', containerId);
    updateMetricByKey(placementId, PlacementStatus.NoElementInDom);
    updatePlacementKeyValueById(placementId, 'status', 'error');
    return;
  }

  const [firstProduct, ...restProducts] = products ?? [];

  // Handle no-fill case
  if (!firstProduct) {
    debugLog('retail_media -> No-fill case for placement:', placementId);
    clearSlot(placementId, containerElement);
    updateMetricByKey(placementId, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(placementId, 'status', 'error');
    return;
  }

  // The carousel is interactive and labels itself; the host's role="img"
  // would hide its buttons and links from assistive tech.
  const host = containerElement.closest('advt-component');
  host?.removeAttribute('role');
  host?.removeAttribute('title');

  // Note: the grid item is the search page's own `.retail-media-ad-wrapper`,
  // not this element, so column spanning is done from CSS
  // (see `.retail-media-ad-wrapper:has(.rm-carousel)`).
  renderIntoSlot(
    placementId,
    containerElement,
    <SponsoredProductCarousel
      products={[firstProduct, ...restProducts]}
      placementId={placementId}
      categories={categories}
    />
  );
}
